// eslint-disable-next-line @eslint-community/eslint-comments/disable-enable-pair
/* eslint-disable react-hooks/refs */
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Children, useEffect, isValidElement, cloneElement, memo } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useInteractions, useClick, useDismiss, useHover, flip, autoUpdate } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';

import { Button } from '../../Controls/Button/index.tsx';
import Icons from '../../Elements/Icons/index.ts';
import { DropdownItem } from './components/index.ts';
import { ANIMATION_DURATION_MS, DEFAULT_Z_INDEX } from './constants.ts';
import { DropdownContextProvider, useDropdownContext } from './context.tsx';
import { getPlacement, stopPropagation } from './helpers.tsx';
import { ButtonWrapper, InnerWrapper, Wrapper } from './styled.ts';

type DropdownVariant = 'secondary' | 'ghost' | 'primary';

export type DropdownListSharedProps = PropsWithChildren<{
  align?: 'left' | 'right';
  zIndex?: number;
  minWidth?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
  isSubmenu?: boolean;
  fixedParent?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  isLoading?: boolean;
}>;

type WithDefaultMoreButton = DropdownListSharedProps & {
  variant?: DropdownVariant;
  iconOnly?: boolean;
  className?: string;
  id?: string;
  renderButton?: never;
  isComboButton?: never;
};

type WithCustomButton = DropdownListSharedProps & {
  renderButton: (isShown: boolean) => ReactNode;
  isComboButton?: boolean;
  variant?: never;
  iconOnly?: never;
  className?: never;
  id?: never;
};

export type DropdownListProps = WithDefaultMoreButton | WithCustomButton;

const isCustomButton = (props: DropdownListProps): props is WithCustomButton =>
  'renderButton' in props && typeof props.renderButton === 'function';

const DropdownListComponent: FC<DropdownListProps> = (props) => {
  const {
    children,
    align = 'left',
    zIndex = DEFAULT_Z_INDEX,
    minWidth = true,
    onVisibilityChange,
    isSubmenu = false,
    fixedParent,
    dataTestId,
    disabled = false,
    isLoading = false,
  } = props;

  const isChildrenArrayEmpty = Array.isArray(children) ? children.every((c) => !!c === false) : false;
  const { isShown, update } = useDropdownContext();

  const { refs, floatingStyles, context, placement } = useFloating({
    open: isShown,
    placement: getPlacement(align, isSubmenu),
    onOpenChange: (open) => {
      update(open);
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(1),
      flip({
        fallbackPlacements: isSubmenu
          ? ['left', 'right-start', 'left-start', 'right-end', 'left-end']
          : ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
      }),
    ],
    strategy: fixedParent ? 'fixed' : 'absolute',
  });
  const isTop = placement.startsWith('top');

  const click = useClick(context, {
    enabled: !isSubmenu && !disabled && !isLoading,
  });
  const hover = useHover(context, {
    enabled: isSubmenu && !disabled && !isLoading,
    delay: { open: 150, close: 100 },
  });
  const dismiss = useDismiss(context, {
    enabled: isShown,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss]);

  useEffect(() => {
    onVisibilityChange?.(isShown);
  }, [isShown, onVisibilityChange]);

  if (isChildrenArrayEmpty) {
    return null;
  }

  let trigger: ReactNode;

  if (isCustomButton(props)) {
    const rendered = props.renderButton(isShown);

    if (isValidElement(rendered)) {
      const baseProps = {
        ...getReferenceProps(),
        ref: refs.setReference,
        disabled: disabled || rendered.props.disabled,
        isLoading: isLoading || rendered.props.isLoading,
        ...(rendered.props.dataTestId ? {} : { dataTestId }),
      };

      if (props.isComboButton) {
        const chevron = <Icons.Chevron key='__chevron' align={isShown ? 'top' : 'bottom'} size={20} />;
        trigger = cloneElement(rendered, baseProps, ...Children.toArray(rendered.props.children), chevron);
      } else {
        trigger = cloneElement(rendered, baseProps);
      }
    } else {
      trigger = null;
    }
  } else {
    const variant: DropdownVariant = props.variant ?? 'secondary';

    trigger = (
      <Button
        {...getReferenceProps()}
        id={props.id}
        className={props.className}
        disabled={disabled}
        isLoading={isLoading}
        {...{ [variant]: true }}
        iconOnly={props.iconOnly ?? true}
        ref={refs.setReference}
        dataTestId={dataTestId}>
        <Icons.More size={20} active={isShown} />
      </Button>
    );
  }

  return (
    <>
      {createPortal(
        <CSSTransition in={isShown} timeout={ANIMATION_DURATION_MS} transitionAppear unmountOnExit>
          <Wrapper
            {...getFloatingProps()}
            style={floatingStyles}
            $zIndex={zIndex}
            $isTop={isTop}
            $visible={isShown}
            ref={refs.setFloating}
            data-testid='dropdown-wrapper'>
            <InnerWrapper $minWidth={minWidth} $zIndex={zIndex} data-testid='dropdown-inner-wrapper'>
              {children}
            </InnerWrapper>
          </Wrapper>
        </CSSTransition>,
        document.body,
      )}
      <ButtonWrapper onClick={stopPropagation}>{trigger}</ButtonWrapper>
    </>
  );
};

const DropdownListWrapper: FC<DropdownListProps> = memo(function DropdownListWrapper(props) {
  return (
    <DropdownContextProvider>
      <DropdownListComponent {...props} />
    </DropdownContextProvider>
  );
});

export const DropdownList = Object.assign(DropdownListWrapper, {
  DropdownItem,
});
